/**
 * Farbwerte der Chat-Styling-Felder — Lesen und Schreiben an EINER Stelle.
 *
 * Vertrag mit dem chat-widget (beide Seiten lesen dieselben Strings, nicht
 * ändern ohne das Widget):
 *  - Einfarbig: `#rrggbb`, oder `#rrggbbaa`, wenn die Deckkraft < 1 ist.
 *    Gelesen wird auch Großschrift, `#rgb`/`#rgba` und ein fehlendes `#`
 *    (so las das Widget schon immer); geschrieben wird immer Kleinschrift.
 *  - Verlauf: `linear-gradient(<A>deg, <stop>, …)`,
 *    `radial-gradient(circle, <stop>, …)` oder
 *    `conic-gradient(from <A>deg, <stop>, …)` mit
 *    `<stop>` = `#rrggbb[aa] <P>%`, P ganzzahlig 0..100, aufsteigend sortiert,
 *    mindestens zwei Stopps. Andere Syntax wird nicht erzeugt.
 *
 * Wo eine einzelne Farbe gebraucht wird (Schrift, Symbol, Rahmen), gilt
 * `solidFallback`: die Farbe selbst bzw. der ERSTE Stopp eines Verlaufs.
 */

export type GradientType = 'linear' | 'radial' | 'conic';

export interface SolidColorValue {
  kind: 'solid';
  /** `#rrggbb`, Kleinschrift. */
  hex: string;
  /** Deckkraft 0..1. */
  alpha: number;
}

export interface GradientStop {
  /** `#rrggbb`, Kleinschrift. */
  hex: string;
  /** Deckkraft 0..1. */
  alpha: number;
  /** Position 0..100 (%). */
  position: number;
}

export interface GradientColorValue {
  kind: 'gradient';
  type: GradientType;
  /** Winkel in Grad (linear / conic); bei radial ohne Bedeutung. */
  angle: number;
  stops: GradientStop[];
}

export type ColorValue = SolidColorValue | GradientColorValue;

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export interface Hsv {
  /** 0..360 */
  h: number;
  /** 0..1 */
  s: number;
  /** 0..1 */
  v: number;
}

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
const GRADIENT_RE = /^(linear|radial|conic)-gradient\(([\s\S]*)\)$/i;

/** Hex in beliebiger erlaubter Schreibweise → `#rrggbb` + Deckkraft. */
export function parseHex(value: string): { hex: string; alpha: number } | null {
  const match = HEX_RE.exec(value.trim());
  if (!match) return null;
  let digits = match[1].toLowerCase();
  if (digits.length <= 4) digits = digits.replace(/./g, (c) => c + c);
  const alpha = digits.length === 8 ? parseInt(digits.slice(6, 8), 16) / 255 : 1;
  return { hex: `#${digits.slice(0, 6)}`, alpha };
}

/** `#rrggbb` + Deckkraft → `#rrggbb` bzw. `#rrggbbaa`. */
export function formatHex(hex: string, alpha = 1): string {
  const parsed = parseHex(hex);
  const base = parsed ? parsed.hex : '#000000';
  const a = clamp(alpha, 0, 1);
  if (a >= 1) return base;
  return base + Math.round(a * 255).toString(16).padStart(2, '0');
}

export function isGradient(value: unknown): boolean {
  return typeof value === 'string' && /^\s*(linear|radial|conic)-gradient\(/i.test(value);
}

export function parseColorValue(value: unknown): ColorValue | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text) return null;

  const solid = parseHex(text);
  if (solid) return { kind: 'solid', hex: solid.hex, alpha: solid.alpha };

  const match = GRADIENT_RE.exec(text);
  if (!match) return null;
  const type = match[1].toLowerCase() as GradientType;
  const args = splitTopLevel(match[2]);
  if (!args.length) return null;

  let angle = type === 'linear' ? 180 : 0;
  const first = args[0].trim().toLowerCase();
  if (type === 'linear' && /^-?\d+(\.\d+)?deg$/.test(first)) {
    angle = parseFloat(first);
    args.shift();
  } else if (type === 'radial' && /^circle$/.test(first)) {
    args.shift();
  } else if (type === 'conic' && /^from\s+-?\d+(\.\d+)?deg$/.test(first)) {
    angle = parseFloat(first.replace(/^from\s+/, ''));
    args.shift();
  }

  const raw: Array<{ hex: string; alpha: number; position: number | null }> = [];
  for (const arg of args) {
    const parts = arg.trim().split(/\s+/);
    if (parts.length < 1 || parts.length > 2) return null;
    const color = parseHex(parts[0]);
    if (!color) return null;
    let position: number | null = null;
    if (parts.length === 2) {
      const pos = /^(-?\d+(\.\d+)?)%$/.exec(parts[1]);
      if (!pos) return null;
      position = parseFloat(pos[1]);
    }
    raw.push({ hex: color.hex, alpha: color.alpha, position });
  }
  if (raw.length < 2) return null;

  // Stopps ohne Position gleichmäßig verteilen (nur für fremde Schreibweise —
  // selbst erzeugt wird immer mit Position).
  const stops: GradientStop[] = raw.map((s, i) => ({
    hex: s.hex,
    alpha: s.alpha,
    position: clampPosition(s.position ?? (i / (raw.length - 1)) * 100),
  }));
  stops.sort((a, b) => a.position - b.position);

  return { kind: 'gradient', type, angle: normalizeAngle(angle), stops };
}

export function serializeColorValue(value: ColorValue): string {
  if (value.kind === 'solid') return formatHex(value.hex, value.alpha);
  const stops = [...value.stops]
    .map((s) => ({ ...s, position: clampPosition(s.position) }))
    .sort((a, b) => a.position - b.position)
    .map((s) => `${formatHex(s.hex, s.alpha)} ${s.position}%`)
    .join(', ');
  const angle = normalizeAngle(value.angle);
  switch (value.type) {
    case 'radial':
      return `radial-gradient(circle, ${stops})`;
    case 'conic':
      return `conic-gradient(from ${angle}deg, ${stops})`;
    default:
      return `linear-gradient(${angle}deg, ${stops})`;
  }
}

/** Eine einzelne Farbe für Schrift, Symbol oder Rahmen: bei Einfarbig der
 *  Wert selbst, bei einem Verlauf der ERSTE Stopp als `#rrggbb`. Leerer
 *  Wert → ''; Unlesbares bleibt unverändert (kann ein CSS-Farbname sein). */
export function solidFallback(value: unknown): string {
  if (typeof value !== 'string') return '';
  const parsed = parseColorValue(value);
  if (!parsed) return value.trim();
  if (parsed.kind === 'solid') return value.trim();
  return parsed.stops[0].hex;
}

/** CSS-Ausgabe mit allen Deckkräften (Einfarbig oder Verlauf) multipliziert
 *  mit `factor` — für Transparenz-Regler. Stopps als rgba(). */
export function toCssWithAlpha(value: ColorValue, factor: number): string {
  const f = clamp(factor, 0, 1);
  const rgba = (hex: string, alpha: number) => {
    const { r, g, b } = hexToRgb(hex);
    return `rgba(${r}, ${g}, ${b}, ${Math.round(alpha * f * 100) / 100})`;
  };
  if (value.kind === 'solid') return rgba(value.hex, value.alpha);
  const stops = [...value.stops]
    .sort((a, b) => a.position - b.position)
    .map((s) => `${rgba(s.hex, s.alpha)} ${clampPosition(s.position)}%`)
    .join(', ');
  const angle = normalizeAngle(value.angle);
  if (value.type === 'radial') return `radial-gradient(circle, ${stops})`;
  if (value.type === 'conic') return `conic-gradient(from ${angle}deg, ${stops})`;
  return `linear-gradient(${angle}deg, ${stops})`;
}

// ---------------------------------------------------------------- Umrechnung

export function hexToRgb(hex: string): Rgb {
  const parsed = parseHex(hex);
  const n = parseInt((parsed?.hex ?? '#000000').slice(1), 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbToHex({ r, g, b }: Rgb): string {
  const part = (x: number) => Math.round(clamp(x, 0, 255)).toString(16).padStart(2, '0');
  return `#${part(r)}${part(g)}${part(b)}`;
}

export function rgbToHsv({ r, g, b }: Rgb): Hsv {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const d = max - min;
  let h = 0;
  if (d !== 0) {
    if (max === rn) h = ((gn - bn) / d) % 6;
    else if (max === gn) h = (bn - rn) / d + 2;
    else h = (rn - gn) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return { h, s: max === 0 ? 0 : d / max, v: max };
}

export function hsvToRgb({ h, s, v }: Hsv): Rgb {
  const c = v * s;
  const hh = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hh % 2) - 1));
  let rgb: [number, number, number] = [0, 0, 0];
  if (hh < 1) rgb = [c, x, 0];
  else if (hh < 2) rgb = [x, c, 0];
  else if (hh < 3) rgb = [0, c, x];
  else if (hh < 4) rgb = [0, x, c];
  else if (hh < 5) rgb = [x, 0, c];
  else rgb = [c, 0, x];
  const m = v - c;
  return {
    r: Math.round((rgb[0] + m) * 255),
    g: Math.round((rgb[1] + m) * 255),
    b: Math.round((rgb[2] + m) * 255),
  };
}

// ------------------------------------------------------------------- intern

function clamp(n: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, Number.isFinite(n) ? n : min));
}

function clampPosition(n: number): number {
  return Math.round(clamp(n, 0, 100));
}

function normalizeAngle(n: number): number {
  const a = Math.round(Number.isFinite(n) ? n : 0) % 360;
  return a < 0 ? a + 360 : a;
}

/** Teilt an Kommas der obersten Ebene (Klammern wie rgba(…) bleiben ganz). */
function splitTopLevel(text: string): string[] {
  const out: string[] = [];
  let depth = 0;
  let current = '';
  for (const ch of text) {
    if (ch === '(') depth++;
    if (ch === ')') depth--;
    if (ch === ',' && depth === 0) {
      out.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  if (current.trim()) out.push(current);
  return out.map((s) => s.trim()).filter(Boolean);
}
